import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getIsAttributeDatatypeModified } from 'src/containers/ProjectDiagram/utils/draft/draftAttribute';
import {
  getIsDescriptionModified,
  getIsLabelModified,
} from 'src/containers/ProjectDiagram/utils/draft/draftModelConcept';
import { getAllInheritedAttributes } from 'src/containers/ProjectDiagram/utils/getAllInheritedAttributes';
import { getClassHierarchy } from 'src/containers/ProjectDiagram/utils/getClassHierarchy';
import {
  getModelConceptClassKey,
  getModelConceptClassValue,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getProjectAttributesByDomain } from 'src/utils/getProjectAttributesByDomain';

export const getPreviousClass = (diagram: DiagramSnapshot, key: string) => {
  const found = diagram.model.modelData.draftData?.prevNodeDataMap[key];
  return found && isModelConceptClass(found) ? found : null;
};

/**
 * Returns a set of attribute IRIs that are determined to be modified relative to
 * a given class in preview mode. The changes that are counted:
 * 1) adding class as domain (directly or through inheritance)
 * 2) updating datatypes (ranges), label, or description
 *
 * @returns {Set<string>} - Set of attribute IRIs
 */
export const getModifiedClassAttributes = ({
  classAttributes,
  classIri,
  isNewClass,
  diagram,
}: {
  classAttributes: Record<string, ModelConceptAttribute>;
  classIri: string;
  isNewClass: boolean;
  diagram: DiagramSnapshot;
}) => {
  // since adding a parent can also change the attributes that are shown for a
  // class (inherited attributes),we also need to check the previous inheritance
  // vs the current (which is already reflected in `classAttributes`)
  const previousAttributesByDomain = getProjectAttributesByDomain(
    diagram.model.modelData.draftData?.prevModelData.attributesByIri || {}
  );
  const previousClassIris = (
    diagram.model.modelData.draftData?.prevNodeDataArray || []
  ).reduce<Set<string>>((acc, nodeData) => {
    if (isModelConceptClass(nodeData)) {
      acc.add(nodeData.value);
    }
    return acc;
  }, new Set());
  const previousHierarchyByIri = getClassHierarchy(
    diagram.model.modelData.draftData?.prevLinkDataArray || [],
    previousClassIris
  );
  const previousClassAttributes = getAllInheritedAttributes({
    classIri,
    hierarchyByIri: previousHierarchyByIri,
    attributesByDomain: previousAttributesByDomain,
  });

  const modifiedAttributes = new Set<string>();
  Object.values(classAttributes).forEach((currentAttribute) => {
    const previousAttribute = previousClassAttributes[currentAttribute.value];
    const isNewAttribute = !previousAttribute;
    if (isNewClass || isNewAttribute) {
      modifiedAttributes.add(currentAttribute.value);
      return;
    }

    const isDescriptionModified = getIsDescriptionModified({
      iri: currentAttribute.value,
      isNewConcept: isNewAttribute,
      diagram,
    });
    const isLabelModified = getIsLabelModified({
      iri: currentAttribute.value,
      isNewConcept: isNewAttribute,
      diagram,
    });
    const isDatatypeModified = getIsAttributeDatatypeModified({
      currentAttribute,
      previousAttribute,
      isNewAttribute,
    });
    if (isDescriptionModified || isLabelModified || isDatatypeModified) {
      modifiedAttributes.add(currentAttribute.value);
    }
  });

  return modifiedAttributes;
};

/**
 * Returns sets of child/parent IRIs that were added for the class in preview mode.
 *
 * @returns {{ addedChildren: Set<string>, addedParents: Set<string> }}
 */
export const getAddedClassHierarchies = ({
  classIri,
  isNewClass,
  diagram,
}: {
  classIri: string;
  isNewClass: boolean;
  diagram: DiagramSnapshot;
}) => {
  const classKey = getModelConceptClassKey(classIri);

  const addedParents = new Set<string>();
  const addedChildren = new Set<string>();

  diagram.model.linkDataArray.forEach((linkData) => {
    if (isModelConceptHierarchy(linkData)) {
      if (linkData.from === classKey) {
        const parentValue = getModelConceptClassValue(linkData.to);
        addedParents.add(parentValue);
      }
      if (linkData.to === classKey) {
        const childValue = getModelConceptClassValue(linkData.from);
        addedChildren.add(childValue);
      }
    }
  });

  if (!isNewClass) {
    diagram.model.modelData.draftData?.prevLinkDataArray.forEach((linkData) => {
      if (isModelConceptHierarchy(linkData)) {
        if (linkData.from === classKey) {
          addedParents.delete(getModelConceptClassValue(linkData.to));
        }
        if (linkData.to === classKey) {
          addedParents.delete(getModelConceptClassValue(linkData.from));
        }
      }
    });
  }

  return { addedChildren, addedParents };
};

/**
 * Returns a set of relationship IRIs that are determined to be modified
 * relative to a given class in preview mode. The changes that are counted:
 * 1) adding class as domain or range
 * 2) removing class from domain or range
 * 3) updating label, or description
 *
 * @returns {Set<string>} - Set of relationship IRIs
 */
export const getModifiedClassRelationships = ({
  classIri,
  isNewClass,
  diagram,
}: {
  classIri: string;
  isNewClass: boolean;
  diagram: DiagramSnapshot;
}) => {
  const classKey = getModelConceptClassKey(classIri);

  const previousRels = new Set<string>();
  const previousIncomingRels = new Set<string>();
  const previousOutgoingRels = new Set<string>();
  diagram.model.modelData.draftData?.prevLinkDataArray.forEach((linkData) => {
    if (isModelConceptRelationship(linkData)) {
      previousRels.add(linkData.value);
      if (linkData.to === classKey) {
        previousIncomingRels.add(linkData.value);
      }
      if (linkData.from === classKey) {
        previousOutgoingRels.add(linkData.value);
      }
    }
  });

  const currentIncomingRels = new Set<string>();
  const currentOutgoingRels = new Set<string>();
  const modifiedRelationships = new Set<string>();
  diagram.model.linkDataArray.forEach((linkData) => {
    if (isModelConceptRelationship(linkData)) {
      const isNewRelationship = !previousRels.has(linkData.value);
      const isDescriptionModified = getIsDescriptionModified({
        iri: linkData.value,
        isNewConcept: isNewRelationship,
        diagram,
      });
      const isLabelModified = getIsLabelModified({
        iri: linkData.value,
        isNewConcept: isNewRelationship,
        diagram,
      });

      if (linkData.to === classKey) {
        currentIncomingRels.add(linkData.value);
        if (
          isNewClass ||
          isNewRelationship ||
          isDescriptionModified ||
          isLabelModified ||
          !previousIncomingRels.has(linkData.value)
        ) {
          modifiedRelationships.add(linkData.value);
        }
      }
      if (linkData.from === classKey) {
        currentOutgoingRels.add(linkData.value);
        if (
          isNewClass ||
          isNewRelationship ||
          isDescriptionModified ||
          isLabelModified ||
          !previousOutgoingRels.has(linkData.value)
        ) {
          modifiedRelationships.add(linkData.value);
        }
      }
    }
  });

  previousIncomingRels.forEach((relationshipIri) => {
    const isDescriptionModified = getIsDescriptionModified({
      iri: relationshipIri,
      isNewConcept: false,
      diagram,
    });
    const isLabelModified = getIsLabelModified({
      iri: relationshipIri,
      isNewConcept: false,
      diagram,
    });

    if (
      isDescriptionModified ||
      isLabelModified ||
      !currentIncomingRels.has(relationshipIri)
    ) {
      modifiedRelationships.add(relationshipIri);
    }
  });
  previousOutgoingRels.forEach((relationshipIri) => {
    const isDescriptionModified = getIsDescriptionModified({
      iri: relationshipIri,
      isNewConcept: false,
      diagram,
    });
    const isLabelModified = getIsLabelModified({
      iri: relationshipIri,
      isNewConcept: false,
      diagram,
    });

    if (
      isDescriptionModified ||
      isLabelModified ||
      !currentOutgoingRels.has(relationshipIri)
    ) {
      modifiedRelationships.add(relationshipIri);
    }
  });

  return modifiedRelationships;
};
